import React, { useMemo, useRef, useState, useEffect } from 'react';
import classNames from 'classnames';
import styled from '@emotion/styled';
import isNil from 'lodash-es/isNil';
import { getHours, getMinutes, set, format } from 'date-fns';
import { useProviderConfig } from '../provider';
import { TimePickerProps, DisabledTimes } from './TimePicker';
import { TimeRangeInput } from './TimeRangeInput';
import { TimePickerOptions } from './TimePickerOptions';
import { TimeChipInput } from './TimeChipInput';
import { Stack } from '../stack';
import { TimePickerOverlay } from './TimePickerOverlay';
import { BottomSheet } from '../bottom-sheet';
import { Divider } from '../divider';
import { Button } from '../button';
import { BaseText } from '../text';
import { time_picker_group_css } from './styles';
import { IconArrowDirectionRight } from '../icons/generated';
import { semantic_colors, semantic_spacing } from '../../foundation';
import { getZIndex } from '../../styles';
import { FormHelperText } from '../form';
import { getAvailableSelectedTime, getDisabledTime, getDisabledTimes, getTimePickerOptions } from './utils';

export type TimeRange = { from: Date | null; to: Date | null };

export interface TimeRangePickerProps extends Omit<TimePickerProps, 'value' | 'onChange' | 'disabledTime'> {
  value?: TimeRange;
  onChange?: (start: TimeRange['from'], end: TimeRange['to']) => void;
  disabledTime?: (value: Date | null, range?: 'from' | 'to') => DisabledTimes;
}

export const TimeRangePicker = ({
  disabled,
  disabledTime,
  size = 'medium',
  value,
  error,
  width = '100%',
  className,
  zIndex = getZIndex('picker'),
  onOpen,
  onClose,
  onChange,
  incrementMinute,
  fill = false,
  showRemoveButton = false,
  displayMode = 'popover',
}: TimeRangePickerProps) => {
  const { locale } = useProviderConfig();
  const [open, setOpen] = useState(false);
  const [showRemoveIcon, setShowRemoveIcon] = useState(false);
  const isBottomSheet = displayMode === 'bottomSheet';
  const closingByConfirmRef = useRef(false);

  const initialTime = useMemo(
    () => ({
      from: value?.from,
      to: value?.to,
    }),
    [value],
  );

  const [selectedTimeHour, setSelectedTimeHour] = useState<{ from?: number; to?: number }>(() => ({
    from: value?.from ? getHours(value.from) : undefined,
    to: value?.to ? getHours(value.to) : undefined,
  }));
  const [selectedTimeMinute, setSelectedTimeMinute] = useState<{ from?: number; to?: number }>(() => ({
    from: value?.from ? getMinutes(value.from) : undefined,
    to: value?.to ? getMinutes(value.to) : undefined,
  }));

  const { fromTimeHourPickerOptions, fromTimeMinutePickerOptions, toTimeHourPickerOptions, toTimeMinutePickerOptions } =
    useMemo(() => {
      const today = new Date();
      const fromCurrentTime = set(today, {
        hours: selectedTimeHour.from ?? undefined,
        minutes: selectedTimeMinute.from ?? undefined,
        seconds: 0,
      });
      const toCurrentTime = set(today, {
        hours: selectedTimeHour.to ?? undefined,
        minutes: selectedTimeMinute.to ?? undefined,
        seconds: 0,
      });

      const fromDisabledTimes = getDisabledTimes(disabledTime, fromCurrentTime, 'from');
      const toDisabledTimes = getDisabledTimes(disabledTime, toCurrentTime, 'to');

      return {
        fromTimeHourPickerOptions: getTimePickerOptions(24, 1, fromDisabledTimes.disabledHours),
        fromTimeMinutePickerOptions: getTimePickerOptions(60, incrementMinute, fromDisabledTimes.disabledMinutes),
        toTimeHourPickerOptions: getTimePickerOptions(24, 1, toDisabledTimes.disabledHours),
        toTimeMinutePickerOptions: getTimePickerOptions(60, incrementMinute, toDisabledTimes.disabledMinutes),
      };
    }, [disabledTime, selectedTimeHour, selectedTimeMinute]);

  const disabledSelectedTime = useMemo(() => {
    const isDisabledTime =
      getDisabledTime(fromTimeHourPickerOptions, selectedTimeHour.from) ||
      getDisabledTime(fromTimeMinutePickerOptions, selectedTimeMinute.from) ||
      getDisabledTime(toTimeHourPickerOptions, selectedTimeHour.to) ||
      getDisabledTime(toTimeMinutePickerOptions, selectedTimeMinute.to);

    if (
      selectedTimeHour.from !== undefined &&
      selectedTimeMinute.from !== undefined &&
      selectedTimeHour.to !== undefined &&
      selectedTimeMinute.to !== undefined
    ) {
      return (
        isDisabledTime ||
        !Boolean(fromTimeMinutePickerOptions.find((option) => option.value === Number(selectedTimeMinute.from))) ||
        !Boolean(toTimeMinutePickerOptions.find((option) => option.value === Number(selectedTimeMinute.to)))
      );
    }

    if (!selectedTimeHour.from || !selectedTimeMinute.from || !selectedTimeHour.to || !selectedTimeMinute.to) {
      return false;
    }

    return Boolean(isDisabledTime);
  }, [
    fromTimeHourPickerOptions,
    fromTimeMinutePickerOptions,
    toTimeHourPickerOptions,
    toTimeMinutePickerOptions,
    selectedTimeHour,
    selectedTimeMinute,
  ]);

  const dateTimes = useMemo(() => {
    const fromBaseDate = initialTime?.from ?? new Date();
    const toBaseDate = initialTime?.to ?? new Date();
    const currentTimes: TimeRange = {
      from: null,
      to: null,
    };

    if (!isNil(selectedTimeHour.from) && !isNil(selectedTimeMinute.from)) {
      currentTimes.from = set(fromBaseDate, {
        hours: selectedTimeHour.from,
        minutes: selectedTimeMinute.from,
        seconds: 0,
      });
    }

    if (!isNil(selectedTimeHour.to) && !isNil(selectedTimeMinute.to)) {
      currentTimes.to = set(toBaseDate, { hours: selectedTimeHour.to, minutes: selectedTimeMinute.to, seconds: 0 });
    }
    return currentTimes;
  }, [selectedTimeHour, selectedTimeMinute]);

  const selectedLabel = useMemo(() => {
    return {
      from: dateTimes.from ? format(dateTimes.from, 'HH:mm') : '',
      to: dateTimes.to ? format(dateTimes.to, 'HH:mm') : '',
    };
  }, [dateTimes]);

  const handleInitial = () => {
    setSelectedTimeHour({
      from: undefined,
      to: undefined,
    });
    setSelectedTimeMinute({
      from: undefined,
      to: undefined,
    });
  };

  const handleChangeHour = (value: number, range: 'from' | 'to') => {
    setSelectedTimeHour((prevSelectedTimeHour) => ({
      ...prevSelectedTimeHour,
      [range]: value,
    }));

    if (!selectedTimeMinute[range]) {
      const availableTimeMinute = getAvailableSelectedTime(
        range === 'from' ? fromTimeMinutePickerOptions : toTimeMinutePickerOptions,
      );
      setSelectedTimeMinute((prevSelectedTimeMinute) => ({ ...prevSelectedTimeMinute, [range]: availableTimeMinute }));
    }
  };

  const handleChangeMinute = (value: number, range: 'from' | 'to') => {
    setSelectedTimeMinute((prevSelectedTimeHour) => ({
      ...prevSelectedTimeHour,
      [range]: value,
    }));

    if (!selectedTimeHour[range]) {
      const availableTimeHour = getAvailableSelectedTime(
        range === 'from' ? fromTimeHourPickerOptions : toTimeHourPickerOptions,
      );
      setSelectedTimeHour((prevSelectedTimeHour) => ({ ...prevSelectedTimeHour, [range]: availableTimeHour }));
    }
  };

  const updateSelectedTime = () => {
    if (!initialTime.from || !initialTime.to) {
      handleInitial();
      return;
    }

    const dateFromHour = getHours(initialTime?.from);
    const dateFromMinute = getMinutes(initialTime?.from);
    const dateToHour = getHours(initialTime?.to);
    const dateToMinute = getMinutes(initialTime?.to);

    setSelectedTimeHour({ from: dateFromHour, to: dateToHour });
    setSelectedTimeMinute({ from: dateFromMinute, to: dateToMinute });
  };

  useEffect(() => {
    updateSelectedTime();
  }, [initialTime]);

  const mobileConfirmDisabled = !dateTimes.from || !dateTimes.to || disabledSelectedTime;
  const mobileRemoveIconVisible = !disabled && Boolean(selectedLabel.from && selectedLabel.to) && showRemoveButton;

  const handleOpenSheet = () => {
    if (disabled) return;
    closingByConfirmRef.current = false;
    onOpen?.();
    setOpen(true);
  };

  const handleSheetClose = () => {
    if (!closingByConfirmRef.current) {
      updateSelectedTime();
    }
    setOpen(false);
    onClose?.();
  };

  const handleConfirm = () => {
    if (mobileConfirmDisabled) return;
    closingByConfirmRef.current = true;
    onChange?.(dateTimes.from, dateTimes.to);
    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  if (isBottomSheet) {
    return (
      <>
        <Stack direction='column' gap={semantic_spacing.helper_text.gap.desktop}>
          <Stack
            direction='row'
            tabIndex={0}
            onClick={handleOpenSheet}
            onKeyDown={(e) => e.key === 'Enter' && !open && handleOpenSheet()}
            width={width}
          >
            <TimeRangeInput
              disabled={disabled}
              status={error || disabledSelectedTime ? 'error' : 'info'}
              size={size}
              fromTime={selectedLabel.from}
              toTime={selectedLabel.to}
              showRemoveIcon={mobileRemoveIconVisible}
              onClickRemoveButton={() => {
                handleInitial();
                onChange?.(null, null);
              }}
              className={classNames('pds-time-range-picker-input', className)}
            />
          </Stack>
          {disabledSelectedTime && <FormHelperText status='error'>다른 시간을 선택해주세요.</FormHelperText>}
        </Stack>
        <BottomSheet
          opened={open}
          onClose={handleSheetClose}
          canDragClose={false}
          zIndex={zIndex}
          contentProps={{ p: '0' }}
        >
          {() => (
            <Stack direction='column' align='stretch'>
              <Stack direction='column' pt={16} pl={16} pr={16}>
                <BaseText kind='Body_14_SemiBold'>{locale?.TimeRangePicker.placeholder[0]}</BaseText>
              </Stack>
              <Stack direction='column' p={16} align='stretch'>
                <TimeChipInput
                  value={dateTimes.from}
                  onChange={(next) => {
                    setSelectedTimeHour((prev) => ({ ...prev, from: getHours(next) }));
                    setSelectedTimeMinute((prev) => ({ ...prev, from: getMinutes(next) }));
                  }}
                  disabledTime={disabledTime ? (v) => disabledTime(v, 'from') : undefined}
                  incrementMinute={incrementMinute}
                />
              </Stack>
              <Divider />
              <Stack direction='column' pt={16} pl={16} pr={16}>
                <BaseText kind='Body_14_SemiBold'>{locale?.TimeRangePicker.placeholder[1]}</BaseText>
              </Stack>
              <Stack direction='column' p={16} align='stretch'>
                <TimeChipInput
                  value={dateTimes.to}
                  onChange={(next) => {
                    setSelectedTimeHour((prev) => ({ ...prev, to: getHours(next) }));
                    setSelectedTimeMinute((prev) => ({ ...prev, to: getMinutes(next) }));
                  }}
                  disabledTime={disabledTime ? (v) => disabledTime(v, 'to') : undefined}
                  incrementMinute={incrementMinute}
                />
              </Stack>
              <Divider />
              <Stack direction='row' gap={8} p={16}>
                <Button size='xlarge' fill onClick={handleCancel}>
                  {locale?.TimePicker.footer.clear}
                </Button>
                <Button kind='primary' size='xlarge' fill disabled={mobileConfirmDisabled} onClick={handleConfirm}>
                  {locale?.TimePicker.footer.ok}
                </Button>
              </Stack>
            </Stack>
          )}
        </BottomSheet>
      </>
    );
  }

  return (
    <TimePickerOverlay
      opened={open}
      setOpened={setOpen}
      onOpen={onOpen}
      onClose={onClose}
      onCloseOutsidePress={updateSelectedTime}
      className={className}
      disabled={disabled}
      fill={fill}
      zIndex={zIndex}
      content={({ close, availableHeight }) => (
        <Stack direction='column' gap={8} css={time_picker_group_css} align={fill ? 'center' : 'flex-end'}>
          <TimeRangeWrapper direction='row' gap={16} width={fill ? '100%' : 'auto'}>
            <TimePickerOptions
              availableHeight={availableHeight}
              timeHourPickerOptions={fromTimeHourPickerOptions}
              timeMinutePickerOptions={fromTimeMinutePickerOptions}
              fill={fill}
              selectedTimeHour={selectedTimeHour.from}
              selectedTimeMinute={selectedTimeMinute.from}
              onChangeHour={(value) => handleChangeHour(value, 'from')}
              onChangeMinute={(value) => handleChangeMinute(value, 'from')}
            />
            <TimeRangeArrowIcon size={10} color={semantic_colors.content.tertiary} />
            <TimePickerOptions
              availableHeight={availableHeight}
              timeHourPickerOptions={toTimeHourPickerOptions}
              timeMinutePickerOptions={toTimeMinutePickerOptions}
              fill={fill}
              selectedTimeHour={selectedTimeHour.to}
              selectedTimeMinute={selectedTimeMinute.to}
              onChangeHour={(value) => handleChangeHour(value, 'to')}
              onChangeMinute={(value) => handleChangeMinute(value, 'to')}
            />
          </TimeRangeWrapper>
          <Stack direction='row' gap={8} p={8} width={fill ? '100%' : 'auto'}>
            <Button
              fill={fill}
              onClick={() => {
                updateSelectedTime();
                close();
              }}
            >
              {locale?.TimePicker.footer.clear}
            </Button>
            <Button
              kind='primary'
              fill={fill}
              disabled={!dateTimes.from || !dateTimes.to || disabledSelectedTime}
              onClick={() => {
                onChange?.(dateTimes.from, dateTimes.to);
                close();
              }}
            >
              {locale?.TimePicker.footer.ok}
            </Button>
          </Stack>
        </Stack>
      )}
    >
      <Stack direction='column' gap={semantic_spacing.helper_text.gap.desktop}>
        <Stack
          direction='row'
          onMouseEnter={() => showRemoveButton && setShowRemoveIcon(true)}
          onMouseLeave={() => showRemoveButton && setShowRemoveIcon(false)}
          width={width}
        >
          <TimeRangeInput
            disabled={disabled}
            status={error || disabledSelectedTime ? 'error' : 'info'}
            size={size}
            fromTime={selectedLabel.from}
            toTime={selectedLabel.to}
            showRemoveIcon={Boolean(selectedLabel.from && selectedLabel.to && showRemoveIcon)}
            onClickRemoveButton={() => {
              handleInitial();
              onChange?.(null, null);
            }}
            className={classNames('pds-time-range-picker-input', className)}
          />
        </Stack>
        {disabledSelectedTime && <FormHelperText status='error'>다른 시간을 선택해주세요.</FormHelperText>}
      </Stack>
    </TimePickerOverlay>
  );
};

const TimeRangeWrapper = styled(Stack)`
  position: relative;
`;

const TimeRangeArrowIcon = styled(IconArrowDirectionRight)`
  position: absolute;
  top: 50%;
  left: calc(50% - 5px);
  z-index: 1;
`;
